import React, { useState, useEffect } from 'react';
import { useDomainConfig } from '@/hooks/useDomainConfig';
import { AdSenseContainer, AdSenseLeaderboard } from './AdSenseComponents';
import { SEO } from './SEO/SEO';
import Header from './Header';
import DownloadForm from './DownloadForm';
import PlatformTrustBar from './PlatformTrustBar';
import VideoInfoDisplay from './VideoInfoDisplay';
import Footer from './Footer';
import { isDevelopment, devLog, createPreviewUrl } from '@/utils/environment';


interface VideoFormat {
  url: string;
  label: string;
  resolution?: string;
  ext: string;
  filesize?: number;
  tbr?: number;
  abr?: number;
  vbr?: number;
  is_audio_only: boolean;
  has_audio: boolean;
}

interface VideoInfo {
  title: string;
  uploader?: string;
  description?: string;
  thumbnail_url?: string;
  duration?: number;
  formats: VideoFormat[];
  is_podcast?: boolean;
  series?: string;
  episode_number?: number;
  season_number?: number;
  platform?: string;
}

interface DownloadState {
  isLoading: boolean;
  error: string | null;
  videoInfo: VideoInfo | null;
  sourceUrl: string | null;
}

interface HomePageProps {
  platform?: string;
}

const HomePage: React.FC<HomePageProps> = ({ platform }) => {
  const config = useDomainConfig();
  const [downloadState, setDownloadState] = useState<DownloadState>({
    isLoading: false,
    error: null,
    videoInfo: null,
    sourceUrl: null
  });

  // Handle URL parameters for pre-filled downloads
  const [prefilledUrl, setPrefilledUrl] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const toggleFAQ = (id: number) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlParam = urlParams.get('url');
    
    // Check for URL from sessionStorage (set by PreviewPage error recovery)
    let sessionUrl = '';
    try {
      sessionUrl = sessionStorage.getItem('refreshExtractUrl') || '';
      if (sessionUrl) {
        // Clear the sessionStorage after retrieving it
        sessionStorage.removeItem('refreshExtractUrl');
        console.log('✅ Retrieved URL from sessionStorage for auto-fill:', sessionUrl.substring(0, 50) + '...');
      }
    } catch (err) {
      console.warn('Could not read from sessionStorage:', err);
    }
    
    // Priority: sessionStorage URL > URL parameter
    const urlToUse = sessionUrl || (urlParam ? decodeURIComponent(urlParam) : '');
    
    if (urlToUse) {
      setPrefilledUrl(urlToUse);
      // Clean up URL parameters
      if (urlParam) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // Update page title when video information is extracted
  useEffect(() => {
    if (downloadState.videoInfo && downloadState.videoInfo.title) {
      const videoTitle = downloadState.videoInfo.title;
      const platform = downloadState.videoInfo.platform || 'Unknown Platform';
      const baseTitle = config.branding.name || config.seo.title || 'Video Downloader';
      
      const dynamicTitle = `${baseTitle} | ${platform} | ${videoTitle}`;
      document.title = dynamicTitle;
    }
  }, [downloadState.videoInfo, config.branding.name, config.seo.title]);

  const handleDownload = async (url: string) => {
    setDownloadState({
      isLoading: true,
      error: null,
      videoInfo: null,
      sourceUrl: url
    });

    try {
      // Generate browser fingerprint for security
      const browserFingerprint = {
        canvas: 'browser_fingerprint_placeholder',
        screen: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        userAgent: navigator.userAgent.substring(0, 100),
        timestamp: Date.now()
      };

      const response = await fetch('/api/download-video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tweet_url: url,
          browser_fingerprint: browserFingerprint,
          user_consent: false // Simplified for now
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to process video');
      }

      if (data.error) {
        setDownloadState({
          isLoading: false,
          error: data.error,
          videoInfo: null,
          sourceUrl: url
        });
      } else if (data.formats && data.formats.length > 0) {
        setDownloadState({
          isLoading: false,
          error: null,
          videoInfo: data,
          sourceUrl: url
        });
      } else {
        setDownloadState({
          isLoading: false,
          error: 'Could not retrieve video details. The response was not in the expected format.',
          videoInfo: null,
          sourceUrl: url
        });
      }
    } catch (error) {
      console.error('Download error:', error);
      setDownloadState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred while processing the video',
        videoInfo: null,
        sourceUrl: url
      });
    }
  };

  const handleFormatSelect = (format: VideoFormat, filename: string) => {
    // Create preview URL parameters
    const previewParams: Record<string, string | number | boolean> = {
      url: encodeURIComponent(format.url),
      title: encodeURIComponent(downloadState.videoInfo?.title || 'Untitled'),
      filename: encodeURIComponent(filename),
      is_audio: format.is_audio_only
    };

    // Add format details for media info display
    if (format.resolution) {
      previewParams.resolution = encodeURIComponent(format.resolution);
    }
    if (format.filesize) {
      previewParams.filesize = format.filesize;
    }
    if (format.tbr || format.abr) {
      previewParams.bitrate = format.tbr || format.abr || 0;
    }

    if (downloadState.sourceUrl) {
      previewParams.source_url = encodeURIComponent(downloadState.sourceUrl);
    }

    // Add thumbnail URL for social media sharing
    if (downloadState.videoInfo?.thumbnail_url) {
      previewParams.thumbnail_url = encodeURIComponent(downloadState.videoInfo.thumbnail_url);
    }

    // Create the preview URL using the utility function
    const previewUrl = createPreviewUrl(previewParams);

    // Debug logging for development
    if (isDevelopment()) {
      devLog('🔍 HomePage Format Selection Debug:', {
        component: 'HomePage',
        format: format,
        filesize: format.filesize,
        filesizeMB: format.filesize ? (format.filesize / (1024 * 1024)).toFixed(1) : null,
        bitrate: format.tbr || format.abr,
        resolution: format.resolution,
        previewParams: previewParams,
        generatedPreviewUrl: previewUrl,
        windowLocation: window.location.href,
        windowOrigin: window.location.origin
      });
    }

    // Construct the full URL manually to avoid any routing issues
    const fullPreviewUrl = `${window.location.protocol}//${window.location.host}${previewUrl}`;
    
    // Open in new tab
    window.open(fullPreviewUrl, '_blank');
  };

  return (
    <>
      <SEO />
      <div
        className="min-h-screen"
        style={{
          backgroundColor: config.branding.colors.background,
          color: config.branding.colors.text.primary
        }}
      >
        <Header />

        {/* Hero Section */}
        <section className="hero-section-container pt-24 pb-4 px-4 sm:px-6 lg:px-8">
          {/* Clean Layout Container - Consistent Width */}
          <div className="max-w-[970px] mx-auto">
            {/* Top Leaderboard Ad */}
            <div className="hidden lg:block mb-8">
              <AdSenseContainer>
                <div className="ad-container">
                  <AdSenseLeaderboard />
                </div>
              </AdSenseContainer>
            </div>

            {/* Main Content Area - Same Width as Ads - Desktop Only */}
            <div className="hidden lg:block text-center">
              {/* Main Heading */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 leading-tight">
                Download Videos & Audio from{' '}
                <span style={{ color: config.branding.colors.primary }}>
                  Any Platform
                </span>
              </h1>

                           {/* Subheading with brand-specific messaging */}
               <div className="text-xl mb-8 max-w-4xl mx-auto leading-relaxed text-gray-600">
                 <p>{config.seo.description}</p>

                {/* Brand-specific additional messaging */}
                {config.branding.targetAudience === 'creators' && (
                  <p className="mt-2 text-lg">
                    Perfect for content creators and social media professionals.
                  </p>
                )}

                {config.branding.targetAudience === 'enterprise' && (
                  <p className="mt-2 text-lg">
                    Enterprise-grade downloading with advanced features for professionals.
                  </p>
                )}

                {config.branding.targetAudience === 'comprehensive' && (
                  <p className="mt-2 text-lg">
                    Complete solution for both video and podcast downloading needs.
                  </p>
                )}
              </div>

              {/* Download Form */}
              <DownloadForm
                onSubmit={handleDownload}
                isLoading={downloadState.isLoading}
                prefilledUrl={prefilledUrl}
                platformHint={platform}
                className="mb-10"
              />

              {/* Platform Trust Bar */}
              <PlatformTrustBar />

              {/* Status Messages */}
              {downloadState.isLoading && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                    <span className="text-blue-700 font-medium">Processing your request...</span>
                  </div>
                </div>
              )}

              {downloadState.error && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 font-medium">{downloadState.error}</p>
                </div>
              )}

              {/* Video Info Display */}
              {downloadState.videoInfo && downloadState.sourceUrl && (
                <VideoInfoDisplay
                  videoInfo={downloadState.videoInfo}
                  sourceUrl={downloadState.sourceUrl}
                  onFormatSelect={handleFormatSelect}
                  className="mt-6"
                />
              )}
            </div>

            {/* Bottom Leaderboard Ad */}
            <div className="hidden lg:block mt-8">
              <AdSenseContainer>
                <div className="ad-container">
                  <AdSenseLeaderboard />
                </div>
              </AdSenseContainer>
            </div>

            {/* Mobile/Tablet Layout (No Ads) */}
            <div className="lg:hidden text-center max-w-4xl mx-auto">
              {/* Main Heading */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 leading-tight">
                Download Videos & Audio from{' '}
                <span style={{ color: config.branding.colors.primary }}>
                  Any Platform
                </span>
              </h1>

                           {/* Subheading with brand-specific messaging */}
               <div className="text-xl mb-8 max-w-4xl mx-auto leading-relaxed text-gray-600">
                 <p>{config.seo.description}</p>

                {/* Brand-specific additional messaging */}
                {config.branding.targetAudience === 'creators' && (
                  <p className="mt-2 text-lg">
                    Perfect for content creators and social media professionals.
                  </p>
                )}

                {config.branding.targetAudience === 'enterprise' && (
                  <p className="mt-2 text-lg">
                    Enterprise-grade downloading with advanced features for professionals.
                  </p>
                )}

                {config.branding.targetAudience === 'comprehensive' && (
                  <p className="mt-2 text-lg">
                    Complete solution for both video and podcast downloading needs.
                  </p>
                )}
              </div>

              {/* Download Form */}
              <DownloadForm
                onSubmit={handleDownload}
                isLoading={downloadState.isLoading}
                prefilledUrl={prefilledUrl}
                platformHint={platform}
                className="mb-10"
              />

              {/* Platform Trust Bar */}
              <PlatformTrustBar />

              {/* Status Messages */}
              {downloadState.isLoading && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                    <span className="text-blue-700 font-medium">Processing your request...</span>
                  </div>
                </div>
              )}

              {downloadState.error && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 font-medium">{downloadState.error}</p>
                </div>
              )}

              {/* Video Info Display */}
              {downloadState.videoInfo && downloadState.sourceUrl && (
                <VideoInfoDisplay
                  videoInfo={downloadState.videoInfo}
                  sourceUrl={downloadState.sourceUrl}
                  onFormatSelect={handleFormatSelect}
                  className="mt-6"
                />
              )}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="pt-8 pb-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: config.branding.colors.surface }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Why Choose {config.branding.name}?
              </h2>
              <p className="text-lg text-gray-600">
                {config.branding.tagline}
              </p>
            </div>

                         <div className="grid md:grid-cols-3 gap-8">
               {[
                 {
                   icon: 'fas fa-bolt',
                   title: 'Lightning Fast',
                   description: 'Download at maximum speed with our optimized servers. No waiting, no delays.'
                 },
                 {
                   icon: 'fas fa-shield-alt',
                   title: '100% Secure',
                   description: 'Your privacy is protected. We never store your content or personal data.'
                 },
                 {
                   icon: 'fas fa-file-download',
                   title: 'All Formats',
                   description: 'MP4, MP3, HD, 4K - choose any format and quality for your needs.'
                 }
               ].map((feature, index) => (
                 <div
                   key={index}
                   className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
                 >
                   <div className="flex items-center mb-4">
                     <div
                       className="w-12 h-12 rounded-lg flex items-center justify-center mr-4"
                       style={{ backgroundColor: config.branding.colors.primary }}
                     >
                       <span className={`${feature.icon} text-white`} />
                     </div>
                     <h3 className="text-xl font-semibold">{feature.title}</h3>
                   </div>
                   <p className="text-gray-600">{feature.description}</p>
                 </div>
               ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">How It Works</h2>
              <p className="text-lg text-gray-600">Download any video in just 3 simple steps</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: 1,
                  icon: 'fas fa-copy',
                  title: 'Copy URL',
                  description: 'Find the video you want to save and copy its URL from any supported platform.'
                },
                {
                  step: 2,
                  icon: 'fas fa-paste',
                  title: 'Paste & Process',
                  description: 'Paste the URL in our tool and let our servers analyze and process the content.'
                },
                {
                  step: 3,
                  icon: 'fas fa-cloud-download-alt',
                  title: 'Download',
                  description: 'Select your preferred format and quality, then download instantly to your device.'
                }
              ].map((step) => (
                <div key={step.step} className="text-center">
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 relative"
                    style={{ backgroundColor: config.branding.colors.primary }}
                  >
                    <span className={`${step.icon} text-white text-3xl`} />
                    <div
                      className="absolute -top-2 -right-2 rounded-full w-8 h-8 flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: config.branding.colors.accent }}
                    >
                      {step.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: config.branding.colors.surface }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">Get answers to common questions about our video downloader</p>
            </div>

            <div className="space-y-4">
              {[
                {
                  id: 1,
                  question: 'What video formats are supported?',
                  answer: 'We support popular formats including MP4, MP3, WEBM, and more. You can choose from various quality options from standard definition to high definition depending on the source video.'
                },
                {
                  id: 2,
                  question: 'Which platforms are supported?',
                  answer: 'We support many popular platforms including YouTube, TikTok, Twitter/X, Facebook, Vimeo, Bilibili, Xiaohongshu, and various podcast platforms like Apple Podcasts, Spotify, and SoundCloud.'
                },
                {
                  id: 3,
                  question: 'Is it safe to use?',
                  answer: 'Yes! We prioritize your privacy and security. We don\'t store your downloaded content or personal information. All downloads are processed securely.'
                },
                {
                  id: 4,
                  question: 'Do I need to install software?',
                  answer: 'No installation required! Our downloader works directly in your web browser. Simply paste the URL and download - it\'s that easy.'
                },
                {
                  id: 5,
                  question: 'Can I download private videos?',
                  answer: 'No, we can only download publicly available content. Private videos, age-restricted content, or videos that require login cannot be downloaded.'
                }
              ].map((faq) => (
                <div key={faq.id} className="border border-gray-200 rounded-lg">
                  <button
                    className="w-full px-6 py-4 text-left font-semibold flex justify-between items-center hover:bg-gray-50 transition-colors"
                    onClick={() => toggleFAQ(faq.id)}
                  >
                    <span>{faq.question}</span>
                    <i className={`fas fa-chevron-down transition-transform duration-300 ${
                      expandedFAQ === faq.id ? 'rotate-180' : ''
                    }`}></i>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    expandedFAQ === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="px-6 pb-4 text-gray-600">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-4">Still have questions?</p>
              <a
                href={`mailto:${config.contact.email}`}
                className="inline-flex items-center px-6 py-3 rounded-lg text-white font-medium transition-colors"
                style={{ backgroundColor: config.branding.colors.primary }}
              >
                <span className="fas fa-envelope mr-2"></span>
                Contact Support
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default HomePage;
